// Klanten: een dossier per klant met alles op een plek. De sleutel is lead_id, dezelfde sleutel die
// offertes, facturen, taken, afspraken, documenten, tickets en abonnementen gebruiken.
// Zo zie je bij elke klant in een oogopslag wat er speelt, en wijzigingen zijn overal tegelijk zichtbaar.
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { laadCatalogus } from "../intake/pakketten";
import OperatieKnop, { useOperatieStatus } from "./OperatieKnop";
import TaakInfo from "./TaakInfo";
import {
  C, eur, eur0, vandaagIso, plusDagen, datumNl, dagenTot, leadNaam, echtBedrijf, isGewonnen, isVerloren, lees,
  Kop, Stat, Chip, Melding, MigratieMelding, Venster, Veld, Rij, Leeg,
} from "./gedeeld";

const OFFERTE_KLEUR = { concept: C.muted, verstuurd: C.blue, geaccepteerd: C.green, afgewezen: C.red, verlopen: C.yellow };

function Sectie({ titel, rechts, children, leeg }) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8 }}>
        <p className="lbl" style={{ margin: 0 }}>{titel}</p>
        {rechts}
      </div>
      {leeg ? <p style={{ fontSize: 12, color: C.dim, margin: 0 }}>{leeg}</p> : children}
    </div>
  );
}

export default function Klanten({ user, setView, startId, wisStartId, openFacturatie }) {
  const [leads, setLeads] = useState([]);
  const [sel, setSel] = useState(null);
  const [zoek, setZoek] = useState("");
  const [filter, setFilter] = useState("klanten");
  const [data, setData] = useState({ offertes: [], facturen: [], taken: [], tickets: [], diensten: [], activiteiten: [], afspraken: [], documenten: [] });
  const [laden, setLaden] = useState(true);
  const [ontbreekt, setOntbreekt] = useState(false);
  const [alleDiensten, setAlleDiensten] = useState([]);
  const [alleFacturen, setAlleFacturen] = useState([]);
  const [catalogus, setCatalogus] = useState([]);
  const [venster, setVenster] = useState(null);
  const [formulier, setFormulier] = useState({});
  const [bericht, setBericht] = useState(null);
  const toon = (tekst, type = "ok") => { setBericht({ tekst, type }); setTimeout(() => setBericht(null), 5000); };

  const laadLijst = useCallback(async () => {
    const [l, d, f] = await Promise.all([
      lees(supabase.from("leads").select("*").order("created_at", { ascending: false })),
      lees(supabase.from("klant_diensten").select("*")),
      lees(supabase.from("admin_docs").select("id,lead_id,amount_incl,payment_status,vervaldatum,doc_type").eq("doc_type", "factuur_verkoop")),
    ]);
    setLeads(l.data); setAlleDiensten(d.data); setAlleFacturen(f.data);
    setOntbreekt(d.ontbreekt || f.ontbreekt);
  }, []);

  useEffect(() => {
    laadLijst();
    laadCatalogus(supabase).then((c) => setCatalogus(c.pakketten || [])).catch(() => {});
  }, [laadLijst]);

  useEffect(() => { if (startId) { setSel(String(startId)); if (wisStartId) wisStartId(); } }, [startId, wisStartId]);

  const [opStatus, verversOp] = useOperatieStatus(data.taken);
  const lead = useMemo(() => leads.find((l) => String(l.id) === String(sel)), [leads, sel]);

  const laadDossier = useCallback(async () => {
    if (!lead) return;
    setLaden(true);
    const id = lead.id;
    const email = (lead.email || "").trim().toLowerCase();
    const [o, f, t, tk, d, a, ev, dc] = await Promise.all([
      lees(supabase.from("crm_offertes").select("*").eq("lead_id", id).order("created_at", { ascending: false })),
      lees(supabase.from("admin_docs").select("*").eq("lead_id", id).eq("doc_type", "factuur_verkoop").order("doc_date", { ascending: false })),
      lees(supabase.from("tasks").select("*").eq("lead_id", id).order("created_at", { ascending: false })),
      email
        ? lees(supabase.from("klantenservice_tickets").select("*").or("lead_id.eq." + id + ",klant_email.ilike." + email).order("created_at", { ascending: false }))
        : lees(supabase.from("klantenservice_tickets").select("*").eq("lead_id", id).order("created_at", { ascending: false })),
      lees(supabase.from("klant_diensten").select("*").eq("lead_id", id).order("created_at", { ascending: false })),
      lees(supabase.from("activities").select("*").eq("lead_id", id).order("created_at", { ascending: false }).limit(25)),
      lees(supabase.from("events").select("*").eq("lead_id", id).order("date", { ascending: false }).limit(10)),
      lees(supabase.from("documents").select("id,title,type,created_at").eq("lead_id", id).order("created_at", { ascending: false }).limit(10)),
    ]);
    setData({ offertes: o.data, facturen: f.data, taken: t.data, tickets: tk.data, diensten: d.data, activiteiten: a.data, afspraken: ev.data, documenten: dc.data });
    setLaden(false);
  }, [lead]);

  useEffect(() => {
    laadDossier();
    if (!lead) return undefined;
    const ch = supabase.channel("klant_" + lead.id)
      .on("postgres_changes", { event: "*", schema: "public", table: "tasks" }, () => laadDossier())
      .on("postgres_changes", { event: "*", schema: "public", table: "activities" }, () => laadDossier())
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_offertes" }, () => laadDossier())
      .on("postgres_changes", { event: "*", schema: "public", table: "admin_docs" }, () => laadDossier())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [laadDossier, lead]);

  // Per klant de maandelijkse omzet en het openstaande bedrag, voor de lijst
  const perKlant = useMemo(() => {
    const m = new Map();
    const pak = (id) => { const k = String(id); if (!m.has(k)) m.set(k, { mrr: 0, open: 0, teLaat: 0 }); return m.get(k); };
    alleDiensten.filter((x) => x.status === "actief").forEach((x) => { pak(x.lead_id).mrr += Number(x.bedrag_maand) || 0; });
    alleFacturen.filter((x) => x.lead_id && x.payment_status !== "betaald").forEach((x) => {
      const k = pak(x.lead_id); k.open += Number(x.amount_incl) || 0;
      if (x.vervaldatum && dagenTot(x.vervaldatum) < 0) k.teLaat += 1;
    });
    return m;
  }, [alleDiensten, alleFacturen]);

  const lijst = useMemo(() => {
    const z = zoek.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter === "klanten" && !(isGewonnen(l) || perKlant.has(String(l.id)))) return false;
      if (filter === "open" && (isGewonnen(l) || isVerloren(l))) return false;
      if (!z) return true;
      return [l.name, l.company, l.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(z));
    });
  }, [leads, zoek, filter, perKlant]);

  async function taakKlaar(t) {
    const { error } = await supabase.from("tasks").update({ status: "done", assignee: null, owner: null }).eq("id", t.id);
    if (error) toon("Mislukt: " + error.message, "fout"); else laadDossier();
  }
  async function nieuweTaak() {
    if (!formulier.titel || !formulier.titel.trim()) return;
    const { error } = await supabase.from("tasks").insert([{ title: formulier.titel.trim(), owner: "furgel", assignee: "furgel", priority: formulier.prio || "MED", status: "todo", due: formulier.due || plusDagen(vandaagIso(), 3), lead_id: lead.id, notes: null }]);
    if (error) { toon("Mislukt: " + error.message, "fout"); return; }
    setVenster(null); setFormulier({}); laadDossier();
  }
  async function nieuwTicket() {
    if (!formulier.onderwerp || !formulier.onderwerp.trim()) return;
    const { error } = await supabase.from("klantenservice_tickets").insert([{
      klant_naam: lead.name, klant_email: lead.email || null, kanaal: formulier.kanaal || "overig", onderwerp: formulier.onderwerp.trim(),
      bericht: formulier.bericht || formulier.onderwerp, status: "open", created_by: (user && user.email) || "furgel@dif3r3nt.nl", lead_id: lead.id,
    }]);
    if (error) { toon("Mislukt: " + error.message, "fout"); return; }
    setVenster(null); setFormulier({}); laadDossier();
  }
  async function nieuwAbonnement() {
    const bedrag = Number(formulier.bedrag);
    if (!formulier.dienst || !formulier.dienst.trim() || !(bedrag > 0)) { toon("Vul dienst en maandbedrag in.", "fout"); return; }
    const { error } = await supabase.from("klant_diensten").insert([{ lead_id: lead.id, dienst: formulier.dienst.trim(), bedrag_maand: bedrag, btw_pct: 21, start: formulier.start || vandaagIso(), status: "actief" }]);
    if (error) { toon("Mislukt: " + error.message, "fout"); return; }
    await supabase.from("activities").insert([{ lead_id: lead.id, type: "Abonnement", icon: "↻", text: "Abonnement gestart: " + formulier.dienst.trim() + " (" + eur(bedrag) + " per maand)", time: "Zojuist" }]);
    setVenster(null); setFormulier({}); laadDossier(); laadLijst();
  }
  async function stopAbonnement(x) {
    if (!window.confirm("Abonnement " + x.dienst + " opzeggen per vandaag?")) return;
    const { error } = await supabase.from("klant_diensten").update({ status: "opgezegd", einde: vandaagIso() }).eq("id", x.id);
    if (error) { toon("Mislukt: " + error.message, "fout"); return; }
    await supabase.from("activities").insert([{ lead_id: lead.id, type: "Abonnement", icon: "↻", text: "Abonnement opgezegd: " + x.dienst, time: "Zojuist" }]);
    laadDossier(); laadLijst();
  }

  // ───────── Lijst ─────────
  if (!lead) {
    const totaalMrr = alleDiensten.filter((x) => x.status === "actief").reduce((s, x) => s + (Number(x.bedrag_maand) || 0), 0);
    return (
      <div>
        <Kop titel="Klanten" sub="Een dossier per klant: offertes, facturen, taken, tickets en abonnementen bij elkaar." />
        {ontbreekt && <MigratieMelding />}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 14 }}>
          <Stat label="Klanten" waarde={leads.filter(isGewonnen).length} />
          <Stat label="MRR (maandelijks terugkerend)" waarde={eur0(totaalMrr)} kleur={C.green} sub={eur0(totaalMrr * 12) + " per jaar"} />
          <Stat label="Actieve abonnementen" waarde={alleDiensten.filter((x) => x.status === "actief").length} />
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          <input style={{ maxWidth: 280 }} placeholder="Zoek op naam, bedrijf of e-mail" value={zoek} onChange={(e) => setZoek(e.target.value)} />
          {[["klanten", "Klanten"], ["open", "Lopende leads"], ["alle", "Alles"]].map(([k, t]) => (
            <span key={k} className={"tab" + (filter === k ? " on" : "")} onClick={() => setFilter(k)}>{t}</span>
          ))}
        </div>
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {lijst.length === 0 && <Leeg tekst="Niets gevonden. Een lead wordt klant zodra een offerte is geaccepteerd of de status op gewonnen staat." />}
          {lijst.map((l) => {
            const k = perKlant.get(String(l.id)) || { mrr: 0, open: 0, teLaat: 0 };
            return (
              <Rij key={l.id} onKlik={() => setSel(String(l.id))} titel={leadNaam(l)}
                meta={<>
                  <span>{l.name}{l.email ? " · " + l.email : ""}</span>
                  {k.mrr > 0 && <span>{eur0(k.mrr) + " per maand"}</span>}
                </>}
                rechts={k.teLaat > 0 ? <Chip tekst={k.teLaat + " te laat"} kleur={C.red} /> : k.open > 0 ? <Chip tekst={eur0(k.open) + " open"} kleur={C.yellow} /> : <Chip tekst={l.status} kleur={isGewonnen(l) ? C.green : isVerloren(l) ? C.red : C.blue} />} />
            );
          })}
        </div>
      </div>
    );
  }

  // ───────── Dossier ─────────
  const actieveDiensten = data.diensten.filter((x) => x.status === "actief");
  const mrr = actieveDiensten.reduce((s, x) => s + (Number(x.bedrag_maand) || 0), 0);
  const openFact = data.facturen.filter((f) => f.payment_status !== "betaald");
  const betaald = data.facturen.filter((f) => f.payment_status === "betaald").reduce((s, f) => s + (Number(f.amount_incl) || 0), 0);
  const openTaken = data.taken.filter((t) => t.status !== "done");
  const openTickets = data.tickets.filter((t) => t.status !== "gesloten" && t.status !== "beantwoord");

  return (
    <div>
      <div style={{ marginBottom: 10 }}><button className="btn bg" onClick={() => setSel(null)}>Terug naar klanten</button></div>
      <Kop titel={leadNaam(lead)} sub={[lead.name !== leadNaam(lead) ? lead.name : null, lead.email, lead.phone, lead.source ? "bron: " + lead.source : null].filter(Boolean).join(" · ")}
        rechts={<>
          <Chip tekst={lead.status} kleur={isGewonnen(lead) ? C.green : isVerloren(lead) ? C.red : C.blue} />
          <button className="btn bp" onClick={() => openFacturatie(lead.id)}>+ Offerte</button>
          <button className="btn bg" onClick={() => setView("crm")}>Open CRM</button>
        </>} />
      {ontbreekt && <MigratieMelding />}
      {bericht && <Melding type={bericht.type}>{bericht.tekst}</Melding>}
      {laden && <p style={{ color: C.muted, fontSize: 12 }}>Dossier laden...</p>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 14 }}>
        <Stat label="Per maand (MRR)" waarde={eur0(mrr)} kleur={mrr ? C.green : C.text} />
        <Stat label="Openstaand" waarde={eur0(openFact.reduce((s, f) => s + (Number(f.amount_incl) || 0), 0))} kleur={openFact.some((f) => f.vervaldatum && dagenTot(f.vervaldatum) < 0) ? C.red : C.text} />
        <Stat label="Totaal betaald" waarde={eur0(betaald)} />
        <Stat label="Open taken" waarde={openTaken.length} />
        <Stat label="Open tickets" waarde={openTickets.length} kleur={openTickets.length ? C.yellow : C.text} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 12, alignItems: "start" }}>
        <div>
          <Sectie titel="Abonnementen" rechts={<button className="btn bg" onClick={() => { setFormulier({}); setVenster("abonnement"); }}>+ Abonnement</button>} leeg={data.diensten.length ? null : "Geen abonnementen. Zorgpakketten uit een geaccepteerde offerte komen hier vanzelf."}>
            {data.diensten.map((x) => (
              <div key={x.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
                <span>{x.dienst} <span style={{ color: C.muted }}>sinds {datumNl(x.start)}</span></span>
                <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {eur(x.bedrag_maand)} <Chip tekst={x.status} kleur={x.status === "actief" ? C.green : C.muted} />
                  {x.status === "actief" && <button className="btn bg" style={{ padding: "3px 8px" }} onClick={() => stopAbonnement(x)}>Opzeggen</button>}
                </span>
              </div>
            ))}
          </Sectie>

          <Sectie titel="Offertes en facturen" leeg={data.offertes.length + data.facturen.length ? null : "Nog geen offertes of facturen."}>
            {data.offertes.map((o) => (
              <div key={o.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
                <span>{o.nummer} {o.titel}</span><span style={{ display: "flex", gap: 8 }}>{eur(o.totaal_excl)} <Chip tekst={o.status} kleur={OFFERTE_KLEUR[o.status]} /></span>
              </div>
            ))}
            {data.facturen.map((f) => (
              <div key={f.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
                <span>{f.factuur_nummer || "Factuur"} <span style={{ color: C.muted }}>{datumNl(f.doc_date)}</span></span>
                <span style={{ display: "flex", gap: 8 }}>{eur(f.amount_incl)} <Chip tekst={f.payment_status === "betaald" ? "betaald" : f.vervaldatum && dagenTot(f.vervaldatum) < 0 ? "te laat" : "open"} kleur={f.payment_status === "betaald" ? C.green : f.vervaldatum && dagenTot(f.vervaldatum) < 0 ? C.red : C.yellow} /></span>
              </div>
            ))}
            <button className="btn bg" style={{ marginTop: 10 }} onClick={() => setView("facturatie")}>Naar Offertes en Facturen</button>
          </Sectie>

          <Sectie titel="Tickets" rechts={<button className="btn bg" onClick={() => { setFormulier({ kanaal: "email" }); setVenster("ticket"); }}>+ Ticket</button>} leeg={data.tickets.length ? null : "Geen tickets."}>
            {data.tickets.map((t) => (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{t.onderwerp}</span><Chip tekst={t.status} kleur={t.status === "open" ? C.yellow : t.status === "gesloten" ? C.muted : C.green} />
              </div>
            ))}
            {data.tickets.length > 0 && <button className="btn bg" style={{ marginTop: 10 }} onClick={() => setView("klantenservice")}>Naar Klantenservice</button>}
          </Sectie>
        </div>

        <div>
          <Sectie titel="Taken" rechts={<button className="btn bg" onClick={() => { setFormulier({ prio: "MED", due: plusDagen(vandaagIso(), 3) }); setVenster("taak"); }}>+ Taak</button>} leeg={data.taken.length ? null : "Geen taken voor deze klant."}>
            {data.taken.slice(0, 12).map((t) => (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
                <span style={{ opacity: t.status === "done" ? 0.5 : 1, minWidth: 0 }}>
                  <span style={{ textDecoration: t.status === "done" ? "line-through" : "none", fontWeight: 600 }}>{t.title}</span>
                  {t.status !== "done" && <TaakInfo taak={t} naAanvullen={laadDossier} />}
                </span>
                <span style={{ display: "flex", gap: 6, alignItems: "center", whiteSpace: "nowrap" }}>
                  {t.due && t.status !== "done" && <Chip tekst={dagenTot(t.due) < 0 ? "te laat" : datumNl(t.due)} kleur={dagenTot(t.due) < 0 ? C.red : C.muted} />}
                  <OperatieKnop taak={t} status={opStatus[String(t.id)]} user={user} setView={setView} compact naVerzenden={async () => { await verversOp(); await laadDossier(); }} />
                  {t.status !== "done" && <button className="btn bg" style={{ padding: "3px 8px" }} onClick={() => taakKlaar(t)}>Klaar</button>}
                </span>
              </div>
            ))}
          </Sectie>

          <Sectie titel="Afspraken en documenten" leeg={data.afspraken.length + data.documenten.length ? null : "Nog geen afspraken of documenten gekoppeld."}>
            {data.afspraken.map((e) => <p key={e.id} style={{ fontSize: 12, margin: "4px 0" }}>{datumNl(e.date)} {e.time} &middot; {e.title}</p>)}
            {data.documenten.map((d) => <p key={d.id} style={{ fontSize: 12, margin: "4px 0", color: C.muted }}>{d.title}</p>)}
          </Sectie>

          <Sectie titel="Tijdlijn" leeg={data.activiteiten.length ? null : "Nog geen activiteiten."}>
            {data.activiteiten.map((a) => (
              <div key={a.id} style={{ display: "flex", gap: 8, padding: "5px 0", fontSize: 12, borderBottom: "1px solid " + C.border }}>
                <span style={{ width: 16, textAlign: "center" }}>{a.icon}</span>
                <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>{String(a.text || "").slice(0, 160)}</span>
                <span style={{ color: C.dim, whiteSpace: "nowrap" }}>{datumNl(a.created_at)}</span>
              </div>
            ))}
          </Sectie>
        </div>
      </div>

      {venster === "taak" && (
        <Venster titel="Nieuwe taak voor deze klant" onSluit={() => setVenster(null)}>
          <Veld label="Wat moet er gebeuren"><input value={formulier.titel || ""} onChange={(e) => setFormulier({ ...formulier, titel: e.target.value })} autoFocus /></Veld>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Veld label="Deadline"><input type="date" value={formulier.due || ""} onChange={(e) => setFormulier({ ...formulier, due: e.target.value })} /></Veld>
            <Veld label="Prioriteit"><select value={formulier.prio} onChange={(e) => setFormulier({ ...formulier, prio: e.target.value })}><option>HIGH</option><option>MED</option><option>LOW</option></select></Veld>
          </div>
          <div style={{ textAlign: "right" }}><button className="btn bp" onClick={nieuweTaak}>Opslaan</button></div>
        </Venster>
      )}
      {venster === "ticket" && (
        <Venster titel="Nieuw ticket voor deze klant" onSluit={() => setVenster(null)}>
          <Veld label="Onderwerp"><input value={formulier.onderwerp || ""} onChange={(e) => setFormulier({ ...formulier, onderwerp: e.target.value })} autoFocus /></Veld>
          <Veld label="Bericht van de klant"><textarea rows={4} value={formulier.bericht || ""} onChange={(e) => setFormulier({ ...formulier, bericht: e.target.value })} /></Veld>
          <Veld label="Kanaal"><select value={formulier.kanaal} onChange={(e) => setFormulier({ ...formulier, kanaal: e.target.value })}><option value="email">E-mail</option><option value="telefoon">Telefoon</option><option value="webshop">Webshop</option><option value="overig">Overig</option></select></Veld>
          <div style={{ textAlign: "right" }}><button className="btn bp" onClick={nieuwTicket}>Opslaan</button></div>
        </Venster>
      )}
      {venster === "abonnement" && (
        <Venster titel="Nieuw abonnement" onSluit={() => setVenster(null)}>
          <Veld label="Dienst">
            <input list="abonnement-opties" value={formulier.dienst || ""} onChange={(e) => {
              const p = catalogus.find((x) => x.naam === e.target.value);
              setFormulier({ ...formulier, dienst: e.target.value, bedrag: p && p.type === "doorlopend" ? p.prijs : formulier.bedrag });
            }} placeholder="Bijvoorbeeld: Zorg Basis" />
            <datalist id="abonnement-opties">{catalogus.filter((p) => p.type === "doorlopend").map((p) => <option key={p.slug} value={p.naam} />)}</datalist>
          </Veld>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Veld label="Bedrag per maand (ex btw)"><input type="number" min="0" step="0.01" value={formulier.bedrag || ""} onChange={(e) => setFormulier({ ...formulier, bedrag: e.target.value })} /></Veld>
            <Veld label="Startdatum"><input type="date" value={formulier.start || vandaagIso()} onChange={(e) => setFormulier({ ...formulier, start: e.target.value })} /></Veld>
          </div>
          <div style={{ textAlign: "right" }}><button className="btn bp" onClick={nieuwAbonnement}>Opslaan</button></div>
        </Venster>
      )}
    </div>
  );
}
